import { UBER_EATS_URL } from "@/config/site";
import { HERO_IMAGES } from "@/data/menu";
import { ArrowRight } from "lucide-react";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative w-full min-h-[100vh] pt-20 sm:pt-24 overflow-hidden bg-brand-bg bg-grain"
    >
      {/* Background hero image with gradient overlay (mobile-first) */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMAGES.main}
          alt="Truffle Cheeseburger"
          className="absolute inset-0 w-full h-full object-cover opacity-40 sm:opacity-50"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/55 to-[#0A0A0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent hidden sm:block" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-5rem)] flex flex-col justify-center py-10 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Copy */}
          <div className="text-center lg:text-left animate-fade-up">
            <span
              className="inline-block font-subheading font-bold uppercase tracking-[0.25em] text-brand-orange text-xs sm:text-sm mb-5 px-4 py-2 border border-brand-orange/30 rounded-full bg-brand-orange/10"
              data-testid="hero-eyebrow"
            >
              Hove · Brighton · Take-away
            </span>
            <h1
              className="font-heading text-white uppercase leading-[0.85] tracking-tight text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]"
              data-testid="hero-headline"
            >
              Smash <span className="text-brand-orange">Burgers.</span>
              <br />
              Fresh <span className="text-brand-green">Baos.</span>
              <br />
              Big Flavours.
            </h1>
            <p
              className="mt-6 sm:mt-8 max-w-xl mx-auto lg:mx-0 font-body text-base sm:text-lg text-white/75 leading-relaxed"
              data-testid="hero-sub"
            >
              Hand-smashed beef. Buttermilk-fried chicken. Pillowy steamed baos. Built fresh in
              Hove, delivered hot to your door.
            </p>

            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start items-center">
              <a
                href={UBER_EATS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-order-cta"
                className="group inline-flex items-center gap-3 bg-brand-orange hover:bg-[#FF7A1A] text-white font-subheading font-bold uppercase tracking-widest text-sm sm:text-base px-8 sm:px-10 py-4 sm:py-5 rounded-full transition-all shadow-[0_8px_30px_0_rgba(255,102,0,0.5)] hover:shadow-[0_12px_40px_0_rgba(255,102,0,0.7)] hover:-translate-y-1"
              >
                Order Now on Uber Eats
                <ArrowRight className="transition-transform group-hover:translate-x-1" size={20} />
              </a>
              <a
                href="#menu"
                data-testid="hero-menu-cta"
                className="inline-flex items-center gap-2 font-subheading font-bold uppercase tracking-widest text-sm text-white/90 hover:text-brand-orange transition-colors px-2 py-2 underline-offset-8 hover:underline"
              >
                See the Menu
              </a>
            </div>
          </div>

          {/* Floating food stack (desktop only — mobile uses bg image already) */}
          <div className="hidden lg:flex relative h-[520px] items-center justify-center">
            <img
              src={HERO_IMAGES.secondary}
              alt="Katsu Bao"
              className="absolute -left-4 top-12 w-64 h-64 object-cover rounded-3xl shadow-2xl border-4 border-white/10 rotate-[-8deg]"
              loading="eager"
            />
            <img
              src={HERO_IMAGES.main}
              alt="Truffle Cheeseburger"
              className="absolute right-0 top-0 w-96 h-96 object-cover rounded-3xl shadow-2xl border-4 border-white/10 rotate-[6deg]"
              loading="eager"
            />
            <img
              src={HERO_IMAGES.tertiary}
              alt="Hoisin Beef Rice Bowl"
              className="absolute right-20 bottom-0 w-60 h-60 object-cover rounded-3xl shadow-2xl border-4 border-white/10 rotate-[-4deg]"
              loading="eager"
            />
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-brand-orange py-3 overflow-hidden">
        <div className="flex whitespace-nowrap animate-ticker font-heading text-2xl tracking-widest text-black">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="px-8 flex items-center gap-8">
              SMASH BURGERS <span className="text-black/40">★</span>
              FRESH BAOS <span className="text-black/40">★</span>
              RICE BOWLS <span className="text-black/40">★</span>
              KOREAN WINGS <span className="text-black/40">★</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
